import { useEffect } from 'react';
import { usePageSEO } from '@/hooks/usePageSEO';
import FadeInSection from '@/components/base/FadeInSection';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import ConnaitreQuartier from './components/ConnaitreQuartier';
import ProfilToitures from './components/ProfilToitures';
import ServicesQuartier from './components/ServicesQuartier';
import PrixTypiques from './components/PrixTypiques';
import ProjetsRecents from './components/ProjetsRecents';
import TemoignageLocal from './components/TemoignageLocal';
import ZonesProches from './components/ZonesProches';
import CTAFinal from './components/CTAFinal';

function setMeta(name: string, content: string) {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function removeOldLdJson() {
  const existing = document.querySelectorAll('script[type="application/ld+json"]');
  existing.forEach((el) => {
    if (el.id === 'stjean-ld') el.remove();
  });
}

const breadcrumbItems = [
  { label: 'Accueil', href: '/' },
  { label: 'Secteurs', href: null },
  { label: 'Saint-Jean-Chrysostome', href: '/secteurs/saint-jean-chrysostome' },
];

export default function StJeanChrysostomePage() {
  usePageSEO({
    title: "Couvreur à Saint-Jean-Chrysostome | Lévis | Quartier Familial | RBQ",
    description: "Couvreur à Saint-Jean-Chrysostome, Lévis. Toiture neuve et réfection pour maisons familiales et nouveaux développements résidentiels. Soumission gratuite en 2h. RBQ. Service 7j/7. 418-476-9950.",
    urlPath: "/secteurs/saint-jean-chrysostome",
    keywords: "couvreur Saint-Jean-Chrysostome, toiture Saint-Jean-Chrysostome, réfection Saint-Jean-Chrysostome, bardeau Lévis, maison neuve, développement résidentiel, soumission",
    ogTitle: "Couvreur à Saint-Jean-Chrysostome — Quartier familial",
    ogDescription: "Toitures neuves et réfections pour les maisons familiales de Saint-Jean-Chrysostome. Plus de 60 projets dans les nouveaux développements résidentiels.",
    ogImage: 'https://couvreur-levis.com/og/og-saintjean.jpg',
    ogImageAlt: 'Couvreur à Saint-Jean-Chrysostome, Lévis - Toitures neuves quartier familial',
    geoPosition: "46.7901;-71.2234",
    geoPlacename: "Saint-Jean-Chrysostome, Lévis, QC",
    geoRegion: "CA-QC",
  });

  useEffect(() => {
    document.title = "Couvreur à Saint-Jean-Chrysostome | Lévis | Quartier Familial | RBQ";
    setMeta('description', "Couvreur à Saint-Jean-Chrysostome, Lévis. Toiture neuve et réfection pour maisons familiales et nouveaux développements résidentiels. Soumission gratuite en 2h. RBQ. Service 7j/7. 418-476-9950.");
    setMeta('keywords', 'couvreur Saint-Jean-Chrysostome, toiture Saint-Jean-Chrysostome, réfection Saint-Jean-Chrysostome, bardeau Lévis, maison neuve, développement résidentiel, soumission');
    setMeta('geo.position', '46.7901;-71.2234');
    setMeta('geo.placename', 'Saint-Jean-Chrysostome, Lévis, QC');
    setMeta('geo.region', 'CA-QC');

    removeOldLdJson();

    const ld = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'LocalBusiness',
          name: 'Couvreur-Lévis.com — Saint-Jean-Chrysostome',
          url: 'https://couvreur-levis.com/secteurs/saint-jean-chrysostome',
          telephone: '+1-418-476-9950',
          email: 'contact@couvreur-levis.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Saint-Jean-Chrysostome',
            addressRegion: 'QC',
            addressCountry: 'CA',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 46.7196,
            longitude: -71.2782,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '07:00',
              closes: '18:00',
            },
          ],
          priceRange: '$$',
          areaServed: {
            '@type': 'Place',
            name: 'Saint-Jean-Chrysostome, Lévis',
          },
          description:
            "Couvreur à Saint-Jean-Chrysostome, Lévis. Toiture neuve et réfection pour maisons familiales et nouveaux développements résidentiels. Soumission gratuite en 2h. RBQ. Service 7j/7. 418-476-9950.",
          image: 'https://couvreur-levis.com/og/og-saintjean.jpg',
          hasMap: 'https://www.google.com/maps?q=Saint-Jean-Chrysostome,L%C3%A9vis,QC',
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbItems.map((item, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            name: item.label,
            item: item.href ? `https://couvreur-levis.com${item.href}` : undefined,
          })),
        },
        {
          '@type': 'Organization',
          name: 'Couvreur-Lévis.com',
          url: 'https://couvreur-levis.com',
          logo: 'https://couvreur-levis.com/og-default.jpg',
          description: 'Plateforme de mise en relation avec des couvreurs certifiés à Lévis et environs.',
          telephone: '+1-418-476-9950',
          email: 'contact@couvreur-levis.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Lévis',
            addressRegion: 'QC',
            addressCountry: 'CA',
          },
        },
        {
          '@type': 'Service',
          serviceType: 'Services de toiture',
          provider: { '@type': 'LocalBusiness', '@id': 'https://couvreur-levis.com/#business' },
          areaServed: {
            '@type': 'Place',
            name: 'Saint-Jean-Chrysostome',
            containedInPlace: { '@type': 'City', name: 'Lévis' },
          },
          description: "Couvreurs partenaires à Saint-Jean-Chrysostome, Lévis. Toiture, réfection, réparation, urgence. Soumission gratuite en 2h.",
        },
      ],
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'stjean-ld';
    script.textContent = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      removeOldLdJson();
    };
  }, []);

  return (
    <div className="font-inter min-h-screen bg-white">
      <Header />
      <main>
        <Hero />
        <FadeInSection delay={100}><ConnaitreQuartier /></FadeInSection>
        <FadeInSection delay={200}><ProfilToitures /></FadeInSection>
        <FadeInSection delay={300}><ServicesQuartier /></FadeInSection>
        <FadeInSection delay={400}><PrixTypiques /></FadeInSection>
        <FadeInSection delay={500}><ProjetsRecents /></FadeInSection>
        <FadeInSection delay={600}><TemoignageLocal /></FadeInSection>
        <FadeInSection delay={700}><ZonesProches /></FadeInSection>
        <FadeInSection delay={800}><CTAFinal /></FadeInSection>
      </main>
      <Footer />
    </div>
  );
}